import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { AlertTriangle, Clock, Loader2, RefreshCw } from 'lucide-react';

type Arm = 'pregnancy' | 'preconception';

type PendingCase = {
  id: string;
  status: 'pending_retry' | 'failed_permanent';
  retry_attempts: number;
  last_retry_at: string | null;
  last_retry_error: string | null;
  tsh: number;
  created_at: string;
  // Pregnancy-only — undefined on preconception rows.
  gestational_week?: number;
};

interface PendingCasesIndicatorProps {
  arm: Arm;
}

const tableMap = {
  pregnancy: {
    cases: 'clinical_cases' as const,
    clinicianResponses: 'clinician_responses' as const,
    selectColumns: 'id,status,retry_attempts,last_retry_at,last_retry_error,tsh,gestational_week,created_at',
  },
  preconception: {
    cases: 'preconception_cases' as const,
    clinicianResponses: 'preconception_clinician_responses' as const,
    selectColumns: 'id,status,retry_attempts,last_retry_at,last_retry_error,tsh,created_at',
  },
};

export const PendingCasesIndicator = ({ arm }: PendingCasesIndicatorProps) => {
  const { user } = useAuth();
  const [cases, setCases] = useState<PendingCase[]>([]);
  const [open, setOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const tables = tableMap[arm];

  const load = useCallback(async () => {
    if (!user) return;
    const { data: profile } = await supabase
      .from('profiles').select('id').eq('user_id', user.id).single();
    if (!profile) return;

    const { data: responses } = await supabase
      .from(tables.clinicianResponses)
      .select('case_id')
      .eq('clinician_id', profile.id);
    const caseIds = (responses ?? []).map(r => r.case_id);
    if (caseIds.length === 0) { setCases([]); return; }

    // Dynamic table name + new columns (status/retry_*) defeat Supabase's
    // type inference; cast through any. Remove after types regenerate.
    const { data, error } = await (supabase
      .from(tables.cases) as any)
      .select(tables.selectColumns)
      .in('id', caseIds)
      .in('status', ['pending_retry', 'failed_permanent'])
      .order('last_retry_at', { ascending: true, nullsFirst: true });
    if (!error) setCases((data ?? []) as PendingCase[]);
  }, [user, tables]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`${arm}_cases_status_changes`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: tables.cases },
        () => load(),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, load, arm, tables.cases]);

  const triggerRetry = async (caseId?: string) => {
    setRetryingId(caseId ?? 'all');
    try {
      const { error } = await supabase.functions.invoke('retry-pending-cases', {
        body: caseId ? { case_id: caseId, arm } : {},
      });
      if (error) throw error;
      toast({ title: caseId ? 'Επανυποβολή υπόθεσης' : 'Επανυποβολή όλων', description: 'Σε εξέλιξη...' });
      setTimeout(load, 1500);
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Αποτυχία επανυποβολής', description: e?.message ?? String(e) });
    } finally {
      setRetryingId(null);
    }
  };

  const pendingCount = cases.filter(c => c.status === 'pending_retry').length;
  const failedCount = cases.filter(c => c.status === 'failed_permanent').length;
  if (pendingCount === 0 && failedCount === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {failedCount > 0 ? (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="w-3 h-3" />
              {failedCount} απέτυχαν
            </Badge>
          ) : null}
          {pendingCount > 0 ? (
            <Badge variant="outline" className="gap-1 border-orange-400 text-orange-700">
              <Clock className="w-3 h-3" />
              {pendingCount} σε εκκρεμότητα
            </Badge>
          ) : null}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Υποθέσεις σε εκκρεμότητα</DialogTitle>
          <DialogDescription>
            Υποθέσεις που η σύσταση του ειδικού καταγράφηκε αλλά η σύγκριση με τον αλγόριθμο δεν ολοκληρώθηκε.
            Η αυτόματη επανυποβολή τρέχει κάθε 5 λεπτά.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {cases.map(c => (
            <div key={c.id} className="flex items-start justify-between gap-3 border rounded-md p-3 text-sm">
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-500">{c.id.slice(0, 8)}…</span>
                  {c.status === 'failed_permanent' ? (
                    <Badge variant="destructive">Απέτυχε ({c.retry_attempts} προσπάθειες)</Badge>
                  ) : (
                    <Badge variant="outline" className="border-orange-400 text-orange-700">
                      Εκκρεμεί ({c.retry_attempts} προσπάθειες)
                    </Badge>
                  )}
                </div>
                <div className="text-gray-700">
                  TSH {c.tsh}
                  {c.gestational_week !== undefined && ` · Εβδ. ${c.gestational_week}`}
                </div>
                {c.last_retry_error && (
                  <div className="text-xs text-red-600 break-words">{c.last_retry_error}</div>
                )}
                <div className="text-xs text-gray-500">
                  Τελευταία προσπάθεια: {c.last_retry_at ? new Date(c.last_retry_at).toLocaleString('el-GR') : '—'}
                </div>
              </div>
              <Button
                size="sm" variant="outline"
                disabled={retryingId === c.id || retryingId === 'all'}
                onClick={() => triggerRetry(c.id)}
              >
                {retryingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                <span className="ml-1">Επανυποβολή</span>
              </Button>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            size="sm"
            disabled={retryingId !== null || pendingCount === 0}
            onClick={() => triggerRetry()}
          >
            {retryingId === 'all' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Επανυποβολή όλων ({pendingCount})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
